// TodayHeader — the mobile Today-tab masthead (C8, bead jonathan-ydfw).
//
// Faithful port of the top of the "TODAY" tab in Jonathan-Mobile.dc.html:
//   - a small olive eyebrow ("Tuesday · for {name}")
//   - a big Playfair greeting ("Morning, {name}.")
//   - a plain-language subtitle ("Two guests today — both already understood.")
//   - a dark "Your week with Jonathan" stat card
//   - an olive reassurance pill ("Nothing risky is booked without your say.")
//
// GROUNDING: the .dc.html's stat grid is mock ("9 / 11 inquiries", "+$1,840",
// "3.5 hrs", "6 after hours"). The dashboard payload does NOT surface those
// metrics, and the locked principle is to ground in real data and never
// fabricate numbers. So the card keeps the design's exact dark-card anatomy
// (eyebrow + 2-col stat grid, olive accent labels) but its cells are derived
// from the ONE real signal the dashboard carries — the inbox feed: how many
// guests are waiting, and how many are brand new.
//
// GREETING (bead jonathan-f7y5): the masthead greets the owner by name when a
// REAL owner name is known. The dashboard payload now surfaces `owner_name`
// (resolved server-side from an owner-stated name or the owner's Square
// given_name — NEVER the business name). It is the only correct name source:
// deriving a first word from the salon name yielded the broken "Afternoon,
// The." for "The Wild Salon". When no owner name is known we render a warm
// name-less greeting ("Good afternoon.") and drop the "for {name}" eyebrow
// tail — we never render a broken token.
//
// The reassurance pill is a static product promise, not a metric, so it is
// kept verbatim.
//
// Reuses C1 tokens/fonts only — no new palette, no dual-path, no numeric
// confidence, no quick-reply chips.

import { ASSISTANT_NAME } from '../brand.js';
function timeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

function weekdayLabel() {
  try {
    return new Date().toLocaleDateString(undefined, { weekday: 'long' });
  } catch {
    return 'Today';
  }
}

// The owner's first name, when a REAL name is known. We never derive a name
// from the salon name (that produced "Afternoon, The." for "The Wild Salon").
function ownerFirstName(name) {
  if (!name) return null;
  const w = String(name).trim().split(/\s+/)[0];
  return w || null;
}

// Plain-language subtitle keyed off the real waiting count — never a number we
// can't back. Mirrors the .dc.html voice ("both already understood").
function subtitleFor(waiting) {
  if (waiting <= 0) return 'A calm board — nothing waiting on you right now.';
  if (waiting === 1) return 'One guest today — already understood.';
  if (waiting === 2) return 'Two guests today — both already understood.';
  return `${waiting} guests today — all already understood.`;
}

function StatCell({ num, label }) {
  return (
    <div>
      <div
        style={{
          fontSize: '27px',
          fontWeight: 800,
          letterSpacing: '-.02em',
          lineHeight: 1,
          marginBottom: '6px',
          color: '#F3EFE6',
        }}
      >
        {num}
      </div>
      <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#E4DECF', lineHeight: 1.3 }}>
        {label}
      </div>
    </div>
  );
}

export function TodayHeader({ ownerName, conversations }) {
  const rows = Array.isArray(conversations) ? conversations : [];
  const waiting = rows.length;
  const fresh = rows.filter((c) => c && c.is_new).length;

  // Greet by name only when a real owner name is known; otherwise a warm
  // name-less greeting ("Good afternoon.") and no "for {name}" eyebrow tail.
  const who = ownerFirstName(ownerName);
  const tod = timeOfDay();
  const greeting = who
    ? `${tod.charAt(0).toUpperCase()}${tod.slice(1)}, ${who}.`
    : `Good ${tod}.`;
  const eyebrow = who ? `${weekdayLabel()} · for ${who}` : weekdayLabel();

  return (
    <div style={{ marginBottom: '22px' }}>
      <div
        style={{
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '.13em',
          textTransform: 'uppercase',
          color: 'var(--olive)',
          marginBottom: '6px',
        }}
      >
        {eyebrow}
      </div>
      <h1
        class="font-serif"
        style={{
          fontWeight: 500,
          fontSize: '30px',
          letterSpacing: '-.02em',
          lineHeight: 1.05,
          color: 'var(--ink)',
          margin: '0 0 6px',
        }}
      >
        {greeting}
      </h1>
      <div
        style={{
          fontSize: '14.5px',
          fontWeight: 500,
          color: 'var(--ink-muted)',
          lineHeight: 1.4,
          marginBottom: '22px',
        }}
      >
        {subtitleFor(waiting)}
      </div>

      {/* dark "Your week with Jonathan" card — design anatomy kept, cells
          grounded in the real inbox feed (no fabricated metrics). */}
      <div style={{ background: 'var(--ink)', borderRadius: '22px', padding: '22px', marginBottom: '14px' }}>
        <div
          style={{
            fontSize: '11.5px',
            fontWeight: 700,
            letterSpacing: '.13em',
            textTransform: 'uppercase',
            color: '#A9B58C',
            marginBottom: '18px',
          }}
        >
          Your week with {ASSISTANT_NAME}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '22px 16px' }}>
          <StatCell num={String(waiting)} label={waiting === 1 ? 'guest understood for you' : 'guests understood for you'} />
          <StatCell num={String(fresh)} label={fresh === 1 ? 'new since you last looked' : 'new since you last looked'} />
        </div>
      </div>

      {/* reassurance pill — a static product promise (verbatim from .dc.html) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '13px 16px',
          background: 'var(--olive-tint)',
          border: '1px solid rgba(86,104,63,.22)',
          borderRadius: '14px',
        }}
      >
        <span
          style={{
            flex: 'none',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'var(--olive)',
            color: '#F3EFE6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 800,
          }}
        >
          ✓
        </span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#3C4730', lineHeight: 1.4 }}>
          Nothing risky is booked without your say.
        </span>
      </div>
    </div>
  );
}
